import React from 'react';

import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { SearchResultTile } from './components/SearchResultTile';
import { pxToRem } from '@withjoy/joykit/theme';
import { productGridStyles } from './SearchResultsGrid.styles';
import { useTranslation } from '@shared/core';
import { GetCatalogProductDetailsQuery, ProductCatalogItemTilePresentationType, ProductCatalogStorefrontStatus, ProductUniversalSearchQuery } from '@graphql/generated';
import { ProductType } from '../../UniversalProdutcSearch.types';
import { ProductTile as ProductTileV2 } from '../../../ProductTileV2';
import { CatalogProductTileItem } from '../../../../Catalog.types';

const ProductGridLayout: React.FC = ({ children }) => {
  return <Box __css={productGridStyles}>{children}</Box>;
};

type OnQuickAddClickArgs = {
  itemPosition: number;
  searchResult: ProductType;
  quantity: number;
};

type SearchResultsGridProps = {
  loading: boolean;
  data: ProductUniversalSearchQuery | null;
  onSearchResultClick: (itemPosition: number, searchResult: ProductType) => void;
  onQuickAddClick: (args: OnQuickAddClickArgs) => void;
  hideHeader: boolean;
  isMobile: boolean;
};

const customProductToCatalogItem = (product: ProductType): GetCatalogProductDetailsQuery['productCatalogItemById'] & CatalogProductTileItem => {
  const photos = product.photos.map(photo => ({ url: photo.url, id: photo.url }));
  const listPriceRange = {
    isSinglePrice: true,
    lowerPrice: product.listPrice,
    upperPrice: product.listPrice
  };
  const salePriceRange = product.salePrice
    ? {
        isSinglePrice: true,
        lowerPrice: product.salePrice,
        upperPrice: product.salePrice
      }
    : undefined;
  const rating = product.rating
    ? {
        id: product.rating.id,
        stats: {
          id: product.rating.id,
          averageRating: product.rating.averageRating,
          totalReviewCount: product.rating.reviewsCount
        }
      }
    : undefined;
  const isFullPrice = !Boolean(product.salePrice);

  return {
    id: product.id,
    title: product.title,
    itemType: 'gift',
    mediaAssets: [],
    storefrontInfo: {
      canAddToCart: true,
      canAddToRegistry: true,
      storefrontStatus: ProductCatalogStorefrontStatus.available
    },
    brand: {
      id: product.storeName,
      name: product.storeName,
      publicUrlPath: new URL(product.url).origin
    },
    rating,
    price: product.salePrice ?? product.listPrice,
    fullPrice: product.listPrice,
    isFullPrice,
    parentCategories: [],
    subCategories: [],
    photos,
    collectionDisplayInfos: [],
    groupDimensionOptions: [],
    tilePresentation: {
      photos,
      presentationType: ProductCatalogItemTilePresentationType.parentDetails,
      title: product.title,
      shortDescription: product.description,
      fullPriceRange: listPriceRange,
      priceRange: salePriceRange ?? listPriceRange,
      isFullPrice
    }
  };
};

const Root = ({
  results,
  onSearchResultClick,
  onQuickAddClick,
  isMobile
}: { results: ProductUniversalSearchQuery; isMobile: boolean } & Pick<SearchResultsGridProps, 'onSearchResultClick' | 'onQuickAddClick'>) => {
  return (
    <ProductGridLayout>
      {results.productUniversalSearch.items
        .map(item => item.universalProduct)
        .map((product: ProductType, idx: number) => {
          const item = customProductToCatalogItem(product);
          return (
            <ProductTileV2
              index={idx}
              key={item.id}
              addItemLoading={false}
              product={item}
              deriveTelemetryPageForProduct={() => 'searchResults'}
              addCatalogItem={args => {
                onQuickAddClick({ itemPosition: idx, searchResult: product, quantity: args.payload.qty });
              }}
              onQuickAddClick={event => {
                event.stopPropagation();
                onQuickAddClick({ itemPosition: idx, searchResult: product, quantity: 1 });
              }}
              onDetailClick={event => {
                event.stopPropagation();
                onSearchResultClick(idx, product);
              }}
              categoryType={'category'}
              isMobile={isMobile}
              hasAnyStorefrontWarning={false}
              componentSource={'productGrid'}
              variant={'gridItem'}
              shouldOpenPdp={false}
              readyProduct={item}
            />
          );
        })}
    </ProductGridLayout>
  );
};

const SKELETON_ARRAY = Array.from(Array(8));

const Skeleton = () => {
  return (
    <ProductGridLayout>
      {SKELETON_ARRAY.map((_, idx) => (
        <SearchResultTile.Skeleton key={idx} />
      ))}
    </ProductGridLayout>
  );
};

const Header = () => {
  const { t } = useTranslation('catalogRegistry');

  const trans = t('universalSearch', 'productgrid');

  return (
    <Flex alignItems="center" flexDirection="column" rowGap={5}>
      <TextV2 typographyVariant="Hed56">{trans.sectionHed()}</TextV2>
      <TextV2 typographyVariant="hed4">{trans.sectionDek()}</TextV2>
    </Flex>
  );
};

export const SearchResultsGrid = (props: SearchResultsGridProps) => {
  const { data, hideHeader, loading, onSearchResultClick, onQuickAddClick, isMobile } = props;

  if (!loading && (!data || !data?.productUniversalSearch?.items?.length)) {
    return null;
  }

  return (
    <Flex flexDirection="column">
      {!hideHeader && <Header />}

      <SkeletonGroup isReady={!loading} marginBottom={pxToRem(131)} placeholder={<Skeleton />}>
        <Box display="flex" flexDirection="column">
          {data && <Root results={data} isMobile={isMobile} onSearchResultClick={onSearchResultClick} onQuickAddClick={onQuickAddClick} />}
        </Box>
      </SkeletonGroup>
    </Flex>
  );
};
